/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { NavigateNext } from "@mui/icons-material";
import { Box, Container, Grid, Link, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Loader from "../Components/Loader/Loader";
import SubscribeButtonPopper from "../Components/SubscribeButtonPopper";
import Legend from "../Containers/Legend";
import Monitor from "../Containers/Monitor";
import OverallStatus from "../Containers/OverallStatus";
import OverallUptime from "../Containers/OverallUptime";
import { useCloud } from "../Hooks/Cloud";
import { useRegion } from "../Hooks/Region";

/**
 * Index page
 * Description: This component is used to display the overall status, overall uptime, monitor groups, and legend
 */
function Index() {
  const { cloud } = useCloud();
  const { region } = useRegion();
  const features = window.ENV[cloud].features;

  const [ monitorGroups, setMonitorGroups ] = useState([]);
  const [ availabilities, setAvailabilities ] = useState([]);
  const [ availabilityHistory, setAvailabilityHistory ] = useState([]);
  const [ isLoadingMonitorGroups, setIsLoadingMonitorGroups ] = useState(true);
  const [ isLoadingAvailabilities, setIsLoadingAvailabilities ] = useState(true);

  // Fetch monitor groups
  const fetchMonitorGroups = () => {
    fetch(`${window.ENV.server.endpoint}/api/v1/monitors?cloud=${cloud}`)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }

        throw new Error(response.status);
      })
      .then((data) => {
        setMonitorGroups(data);
        setIsLoadingMonitorGroups(false);
      })
      .catch(() => {
        // If the request fails, try again after 5 seconds
        setTimeout(fetchMonitorGroups, 5000);
      });
  };

  // Fetch availabilities
  const fetchAvailabilities = () => {
    fetch(`${window.ENV.server.endpoint}/api/v1/availabilities?cloud=${cloud}`)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }

        throw new Error(response.status);
      })
      .then((data) => {
        setAvailabilities(data);
        setIsLoadingAvailabilities(false);
      })
      .catch(() => {
        // If the request fails, try again after 5 seconds
        setTimeout(fetchAvailabilities, 5000);
      });
  };

  // Fetch availability history
  const fetchAvailabilityHistory = () => {
    fetch(`${window.ENV.server.endpoint}/api/v1/history/availabilities?cloud=${cloud}`)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }

        throw new Error(response.status);
      })
      .then((data) => {
        setAvailabilityHistory(data);
      })
      .catch(() => {
        // If the request fails, try again after 5 seconds
        setTimeout(fetchAvailabilityHistory, 5000);
      });
  };

  useEffect(() => {
    // Fetch monitors and availabilities
    fetchMonitorGroups();
    fetchAvailabilities();
    features.availabilityHistory && fetchAvailabilityHistory();

    // Fetch monitors and availabilities in an interval
    const fetchInterval = setInterval(() => {
      fetchMonitorGroups();
      fetchAvailabilities();
      features.availabilityHistory && fetchAvailabilityHistory();
    }, window.ENV.pollingInterval);

    return () => {
      // Clean up the interval when the component is unmounted
      clearInterval(fetchInterval);
    };
  }, [ cloud ]);

  useEffect(() => {
    if (region) {
      const element = document.getElementById("region-container");

      if (element) {
        element.classList.add("animate-region-change");

        const animationEnd = () => {
          element.classList.remove("animate-region-change");
          element.removeEventListener("animationend", animationEnd);
        };

        element.addEventListener("animationend", animationEnd);
      }
    }
  }, [ region ]);

  // Get the overall status message from the monitor groups
  const getOverallStatusMessage = (groups) => {
    let maxStatus = -1;
    let maxMessage = "";

    groups.forEach(group => {
      group.subgroups.forEach(subgroup => {
        if (subgroup.status > maxStatus) {
          maxStatus = subgroup.status;
          maxMessage = subgroup.message;
        }
      });
    });

    return maxMessage;
  };

  // Show a loader if the monitor groups or availabilities are still loading
  if (isLoadingMonitorGroups || isLoadingAvailabilities) {
    return <Loader />;
  }

  return (
    <>
      <Container id="region-container" maxWidth="md">
        { /* Render the overall status container */ }
        <OverallStatus
          status={ Math.max(
            ...monitorGroups[region].map((group) => Math.max(...group.subgroups.map((sgroup) => sgroup.status)))
          ) }
          message={ getOverallStatusMessage(monitorGroups[region]) }
        />

        { /* Display the overall uptime of the services */ }
        <OverallUptime uptime={ availabilities[region] } />

        { /* Render the details of the monitors and sub-groups */ }
        <Monitor
          availabilityHistory={ availabilityHistory[region] || [] }
          monitorGroup={ monitorGroups[region] } />

        { /* Render the legend */ }
        { features.legend && <Legend /> }

        { /* Display the navigation to show the incident history */ }
        { (features.history || features.feed) && (
          <Container>
            <Grid container sx={ { alignItems: "center", display: "flex" } } >
              { features.history && (
                <Grid item rowSpacing={ 2 } xs={ 12 } md sm lg>
                  <Box sx={ { display: "flex", justifyContent: "flex-start" } }>
                    <Link href={ "/history" } variant="body2" underline="none">
                      <Typography component={ "span" } variant="inherit">
                        <Box component="span" sx={ { alignItems: "center", display: "flex" } }>
                          <Box component="span" sx={ { pr: 1 } }>
                            Show Incident History
                          </Box>
                          <NavigateNext />
                        </Box>
                      </Typography>
                    </Link>
                  </Box>
                </Grid>
              ) }
              { features.subscribe && (
                <Grid item rowSpacing={ 2 }>
                  <SubscribeButtonPopper />
                </Grid>
              ) }
            </Grid>
          </Container>
        ) }
      </Container>
    </>
  );
}

export default Index;
